// src/components/mpi/D2RampupDrillDown.js
import { useEffect, useState, useRef } from 'react';
import { mentoringApi as axios } from '../../axiosInstance';

function AnimatedBar({ value, color, delay = 0 }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setWidth(value ?? 0), delay + 60);
    return () => clearTimeout(id);
  }, [value, delay]);
  return (
    <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
      <div style={{
        height: '100%', borderRadius: 3,
        width: `${width}%`,
        background: color,
        boxShadow: `0 0 8px ${color}66`,
        transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
      }} />
    </div>
  );
}

const BUCKET_META = {
  fast:      { label: 'Schnell',      color: '#22c55e' },
  medium:    { label: 'Mittel',       color: '#f59e0b' },
  slow:      { label: 'Langsam',      color: '#f97316' },
  very_slow: { label: 'Sehr langsam', color: '#ef4444' },
};

const MILESTONE_LABELS = {
  kennenlerntermin: 'Kennenlernen',
  '3mon': '3 Monate',
  '6mon': '6 Monate',
  '9mon': '9 Monate',
  '12mon': '12 Monate',
};

function scoreColor(v) {
  if (v >= 4) return '#22c55e';
  if (v >= 3) return '#f59e0b';
  return '#ef4444';
}

export default function D2RampupDrillDown({ period: initialPeriod, tenant, onClose, surveyMode: surveyModeProp = false, d2Score: d2ScoreProp = null, funktionSplit = null }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [checkin, setCheckin] = useState(null);
  const boxRef = useRef(null);

  // Survey-Modus (props-getrieben, D4-Muster). Default false -> alte Aufrufer unveraendert.
  const surveyMode = surveyModeProp === true;
  const d2SurveyScore = d2ScoreProp != null ? Number(d2ScoreProp) : null;
  const d2ScoreColor = d2SurveyScore == null ? '#64748B'
    : d2SurveyScore >= 70 ? '#22c55e' : d2SurveyScore >= 40 ? '#f59e0b' : '#ef4444';

  useEffect(() => {
    let alive = true;
    let url = '/mpi/rampup-summary';
    const qp = [];
    if (initialPeriod) qp.push(`period=${encodeURIComponent(initialPeriod)}`);
    if (tenant) qp.push(`tenant=${encodeURIComponent(tenant)}`);
    if (qp.length) url += '?' + qp.join('&');

    axios.get(url, { withCredentials: true })
      .then(res => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setError('Ramp-Up-Daten konnten nicht geladen werden.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [initialPeriod, tenant]);

  // B3: Check-in-Signale (additiv, eigener Fetch; Contract wirft nie -> catch -> null).
  useEffect(() => {
    let alive = true;
    const tq = tenant ? `?tenant=${encodeURIComponent(tenant)}` : '';
    axios.get(`/mpi/checkin-summary${tq}`, { withCredentials: true })
      .then(r => { if (alive) setCheckin(r.data ?? null); })
      .catch(() => { if (alive) setCheckin(null); });
    return () => { alive = false; };
  }, [tenant]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleOverlayClick(e) {
    if (boxRef.current && !boxRef.current.contains(e.target)) onClose();
  }

  // ── Data extraction ──
  const period       = data?.period ?? initialPeriod ?? '';
  const menteesTotal = data?.mentees_total ?? 0;
  const rampup       = data?.rampup ?? {};
  const learning     = data?.learning ?? {};
  const compDelta    = data?.competency_delta ?? {};
  const mileCov      = data?.milestone_coverage ?? {};
  const interventions = data?.interventions ?? {};
  const freqQuality  = data?.frequency_quality ?? {};

  // Ramp-Up
  const avgDays       = rampup.avg_days != null ? parseFloat(rampup.avg_days) : null;
  const benchmarkDays = rampup.benchmark_days != null ? parseFloat(rampup.benchmark_days) : 14;
  const buckets       = rampup.buckets ?? {};
  const bucketEntries = ['fast', 'medium', 'slow', 'very_slow']
    .map(key => ({ key, ...(buckets[key] ?? { count: 0 }), ...BUCKET_META[key] }))
    .filter(b => b.count > 0 || ['fast', 'medium', 'slow'].includes(b.key));
  const totalBuckets  = bucketEntries.reduce((s, b) => s + (b.count ?? 0), 0);

  // Avg days color
  const avgDaysColor = avgDays != null
    ? (avgDays <= benchmarkDays ? '#22c55e' : avgDays <= benchmarkDays * 2 ? '#f59e0b' : '#ef4444')
    : '#475569';

  // Learning
  const milestones    = learning.milestones ?? [];
  const learningDelta = learning.delta != null ? parseFloat(learning.delta) : null;

  // Competency
  const compAvgDelta  = compDelta.avg_delta != null ? parseFloat(compDelta.avg_delta) : null;
  const compNorm      = compDelta.normalized != null ? parseFloat(compDelta.normalized) : null;
  const compTrend     = compDelta.trend ?? null;
  const compByCat     = compDelta.by_category ?? [];
  const menteesWithDelta = data?.mentees_with_delta ?? 0;

  const trendColor = compTrend === 'UP' ? '#10B981' : compTrend === 'DOWN' ? '#EF4444' : '#F59E0B';
  const trendArrow = compTrend === 'UP' ? '▲' : compTrend === 'DOWN' ? '▼' : '→';

  // Frequency/Quality
  const avgSessions = freqQuality.avg_sessions_per_month != null ? parseFloat(freqQuality.avg_sessions_per_month) : null;
  const avgRating   = freqQuality.avg_rating != null ? parseFloat(freqQuality.avg_rating) : null;
  const freqScore   = freqQuality.freq_score != null ? parseFloat(freqQuality.freq_score) : null;
  const qualScore   = freqQuality.quality_score != null ? parseFloat(freqQuality.quality_score) : null;

  // Interventions
  const intCount = interventions.count ?? 0;

  // Milestone coverage entries
  const coverageEntries = [
    { key: 'kennenlerntermin', label: 'Kennenlerngespräch' },
    { key: '3mon',  label: '3-Monats-Gespräch' },
    { key: '6mon',  label: '6-Monats-Gespräch' },
    { key: '9mon',  label: '9-Monats-Gespräch' },
    { key: '12mon', label: '12-Monats-Gespräch' },
  ].map(e => {
    const count = mileCov[e.key] ?? 0;
    const pct = menteesTotal > 0 ? count / menteesTotal * 100 : 0;
    return { ...e, count, pct };
  });

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        ref={boxRef}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 920,
          maxHeight: '88vh',
          background: 'linear-gradient(160deg, #111827 0%, #080b12 100%)',
          border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 20,
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 32px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04)',
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#F59E0B', boxShadow: '0 0 10px #F59E0B',
              flexShrink: 0, marginTop: 5,
            }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'white', letterSpacing: '-0.3px' }}>
                D2 · Ramp-Up &amp; Lernen — Drill-Down
              </div>
              <div style={{ marginTop: 5, display: 'flex', gap: 14, fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                {period && <span>{period}</span>}
                {surveyMode
                  ? <span>Anonyme Kulturumfrage</span>
                  : <span>{menteesTotal} Mentees gesamt</span>}
                {!surveyMode && menteesWithDelta > 0 && (
                  <span>{menteesWithDelta} mit Kompetenz-Daten</span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, color: 'rgba(255,255,255,0.45)',
              cursor: 'pointer', padding: '6px 12px', fontSize: 11, flexShrink: 0,
            }}
          >
            ✕ Schließen
          </button>
        </div>

        {/* ── Body (scrollable) ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {loading && (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.18em' }}>
              LADE RAMP-UP-DATEN …
            </div>
          )}
          {error && (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 12, color: '#ef4444' }}>{error}</div>
          )}

          {!loading && !error && (
            <>
              {/* ── Block 0: Survey-Modus (D2 aus Kulturumfrage) ── */}
              {surveyMode && (
                <div style={{
                  marginBottom: 24, padding: '20px',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14,
                }}>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16 }}>
                    Ramp-Up (Kulturumfrage)
                  </div>
                  {(() => {
                    const fs = funktionSplit;
                    const barColor = (v) => v == null ? '#64748B' : v >= 70 ? '#22c55e' : v >= 40 ? '#f59e0b' : '#ef4444';
                    if (fs && fs.verfuegbar === true && Array.isArray(fs.funktionen) && fs.funktionen.length > 0) {
                      return (
                        <>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                            {fs.funktionen.map((f, i) => {
                              const v = f.wert != null ? Number(f.wert) : null;
                              return (
                                <div key={f.funktion ?? i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <div title={f.gruppe ? `${f.funktion} (${f.gruppe})` : f.funktion}
                                       style={{ width: 130, fontSize: 11, color: 'rgba(255,255,255,0.6)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {f.funktion}
                                  </div>
                                  <AnimatedBar value={v ?? 0} color={barColor(v)} delay={i * 60} />
                                  <div style={{ fontSize: 11, color: barColor(v), fontWeight: 600, minWidth: 78, textAlign: 'right' }}>
                                    {v != null ? v.toFixed(1).replace('.', ',') : '—'} <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>· n={f.n ?? 0}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {fs.hinweis && (
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>{fs.hinweis}</div>
                          )}
                        </>
                      );
                    }
                    // verfuegbar:false ODER kein Split -> bestehende grosse Zahl + grund-Hinweis
                    const grundText = fs && fs.verfuegbar === false
                      ? (fs.grund === 'unter_schwelle' ? 'Stichprobe zu klein für Aufschlüsselung' : 'keine Funktionsdaten')
                      : null;
                    return (
                      <>
                        {d2SurveyScore != null && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{
                              fontSize: 48, fontWeight: 800, letterSpacing: '-2px', lineHeight: 1,
                              color: d2ScoreColor,
                              fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                            }}>
                              {d2SurveyScore.toFixed(0)}
                            </div>
                            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
                              Aus Kulturumfrage
                            </div>
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
                          {grundText || 'D2 basiert aktuell auf der Kulturumfrage. CRM- und HR-Ramp-up-Signale folgen mit der Datenanbindung.'}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* ── Check-in-Signale (additiv, beruehrt den D2-Score nicht) ── */}
              {checkin && checkin.verfuegbar === true && (
                <div style={{ marginBottom: 24, padding: '20px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14 }}>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16 }}>
                    Kontinuierliche Check-in-Signale
                  </div>
                  {checkin.regelmaessigkeit && checkin.regelmaessigkeit.pct != null && (() => {
                    const pct = Number(checkin.regelmaessigkeit.pct);
                    const col = pct >= 70 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#ef4444';
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <div style={{ width: 150, fontSize: 11, color: 'rgba(255,255,255,0.6)', flexShrink: 0 }}>Check-in-Regelmäßigkeit</div>
                        <AnimatedBar value={pct} color={col} delay={0} />
                        <div style={{ fontSize: 11, color: col, fontWeight: 600, minWidth: 92, textAlign: 'right' }}>
                          {pct.toFixed(1).replace('.', ',')}% <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>· {checkin.regelmaessigkeit.aktiv ?? 0} von {checkin.regelmaessigkeit.basis ?? 0}</span>
                        </div>
                      </div>
                    );
                  })()}
                  {checkin.confidence && checkin.confidence.score != null && (() => {
                    const PATTERN = { monoton_falling: 'fallend', monoton_rising: 'steigend', stable: 'stabil', stabil: 'stabil' };
                    const pat = PATTERN[checkin.confidence.pattern] || checkin.confidence.pattern;
                    const sc = Number(checkin.confidence.score);
                    const col = sc >= 70 ? '#22c55e' : sc >= 40 ? '#f59e0b' : '#ef4444';
                    return (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Confidence</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: col, letterSpacing: '-0.5px' }}>{sc.toFixed(0)}</div>
                        {pat && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>· Verlauf: {pat}</div>}
                      </div>
                    );
                  })()}
                  {checkin.hinweis && (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>{checkin.hinweis}</div>
                  )}
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6, marginTop: 4 }}>
                    Fließen mit voller Datenanbindung in den Ramp-Up-Score ein.
                  </div>
                </div>
              )}

              {!surveyMode && (<>
              {/* ── Block 1: Ramp-Up Geschwindigkeit ── */}
              <div style={{
                marginBottom: 24, padding: '20px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 14,
              }}>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16 }}>
                  Ramp-Up Geschwindigkeit
                </div>

                {/* Ø Days + Benchmark */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 20 }}>
                  {avgDays != null && (
                    <>
                      <div>
                        <div style={{
                          fontSize: 48, fontWeight: 800, letterSpacing: '-2px', lineHeight: 1,
                          color: avgDaysColor,
                          fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                        }}>
                          {avgDays.toFixed(1)}
                        </div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
                          Ø Tage bis Kennenlernen
                        </div>
                      </div>
                      <div style={{ paddingBottom: 8 }}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                          Benchmark: <span style={{ color: '#22c55e', fontWeight: 600 }}>≤ {benchmarkDays} Tage</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Stacked bar */}
                {totalBuckets > 0 && (
                  <>
                    <div style={{
                      display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden',
                      background: 'rgba(255,255,255,0.05)', marginBottom: 12,
                    }}>
                      {bucketEntries.map(b => {
                        const pct = totalBuckets > 0 ? (b.count / totalBuckets * 100) : 0;
                        if (pct === 0) return null;
                        return (
                          <div key={b.key} style={{
                            width: `${pct}%`, background: b.color,
                            boxShadow: `0 0 8px ${b.color}66`,
                            transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
                          }} />
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {bucketEntries.map(b => (
                        <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: b.color, boxShadow: `0 0 6px ${b.color}88` }} />
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                            <span style={{ color: b.color, fontWeight: 600 }}>{b.count}</span> {b.label}
                          </span>
                          {b.label && (
                            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)' }}>
                              {buckets[b.key]?.label}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* ── Block 2: Lernfortschritt & Meilensteine ── */}
              <div style={{
                marginBottom: 24, padding: '20px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                    Lernfortschritt &amp; Meilensteine
                  </div>
                  {learningDelta != null && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '3px 10px', borderRadius: 20,
                      background: learningDelta >= 0 ? '#10B98114' : '#EF444414',
                      border: `1px solid ${learningDelta >= 0 ? '#10B98140' : '#EF444440'}`,
                      fontSize: 11, fontWeight: 600,
                      color: learningDelta >= 0 ? '#10B981' : '#EF4444',
                    }}>
                      <span style={{ fontSize: 9 }}>{learningDelta >= 0 ? '▲' : '▼'}</span>
                      {learningDelta >= 0 ? '+' : ''}{learningDelta.toFixed(1)} Verbesserung
                    </div>
                  )}
                </div>

                {/* Milestone scores */}
                {milestones.length > 0 && (
                  <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
                    {milestones.map((m, i) => {
                      const score = m.avg_score != null ? parseFloat(m.avg_score) : null;
                      const col = score != null ? scoreColor(score) : '#475569';
                      return (
                        <div key={m.name ?? i} style={{
                          flex: 1, minWidth: 100,
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.07)',
                          borderRadius: 12, padding: '14px',
                        }}>
                          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                            {MILESTONE_LABELS[m.name] ?? m.name}
                          </div>
                          <div style={{
                            fontSize: 32, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1,
                            color: col,
                            fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                          }}>
                            {score != null ? score.toFixed(1) : '—'}
                            {score != null && <span style={{ fontSize: 12, fontWeight: 400, color: 'rgba(255,255,255,0.25)' }}> / 5</span>}
                          </div>
                          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', marginTop: 5 }}>
                            {m.count ?? 0} Gespräche
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Milestone coverage */}
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 10 }}>
                  Meilenstein-Abdeckung
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {coverageEntries.map((e, i) => (
                    <div key={e.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 130, fontSize: 10, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
                        {e.label}
                      </div>
                      <AnimatedBar
                        value={e.pct}
                        color={e.count > 0 ? '#F59E0B' : '#475569'}
                        delay={i * 60}
                      />
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap', minWidth: 90, textAlign: 'right' }}>
                        {e.count} / {menteesTotal} ({e.pct.toFixed(1)}%)
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Block 3: Kompetenz-Entwicklung & Gesprächsqualität ── */}
              <div style={{
                marginBottom: 24, padding: '20px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 14,
              }}>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16 }}>
                  Kompetenz-Entwicklung &amp; Gesprächsqualität
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {/* Left: Kompetenz-Delta */}
                  <div>
                    {compAvgDelta != null && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 6 }}>
                          <div style={{
                            fontSize: 36, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1,
                            color: trendColor,
                            fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                          }}>
                            {compAvgDelta >= 0 ? '+' : ''}{compAvgDelta.toFixed(2)}
                          </div>
                          {compTrend && (
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 3,
                              padding: '2px 8px', borderRadius: 20,
                              background: `${trendColor}14`, border: `1px solid ${trendColor}40`,
                              fontSize: 10, color: trendColor, fontWeight: 600,
                              marginBottom: 4,
                            }}>
                              <span style={{ fontSize: 9 }}>{trendArrow}</span>
                              {compTrend === 'UP' ? 'Aufwärts' : compTrend === 'DOWN' ? 'Abwärts' : 'Stabil'}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                          Ø Kompetenz-Delta · {menteesWithDelta} von {menteesTotal} Mentees mit Daten
                        </div>
                      </div>
                    )}

                    {/* Category bars */}
                    {compByCat.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        {compByCat.map((cat, i) => {
                          const val = parseFloat(cat.avg_delta ?? 0);
                          const barColor = val > 0 ? '#10B981' : val < 0 ? '#EF4444' : '#475569';
                          const maxAbs = Math.max(...compByCat.map(c => Math.abs(parseFloat(c.avg_delta ?? 0))), 1);
                          const barPct = Math.min(Math.abs(val) / maxAbs * 100, 100);
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                              <div style={{ width: 70, fontSize: 10, color: 'rgba(255,255,255,0.45)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {cat.category}
                              </div>
                              <AnimatedBar value={barPct} color={barColor} delay={i * 60} />
                              <div style={{ fontSize: 10, color: barColor, fontWeight: 600, minWidth: 36, textAlign: 'right' }}>
                                {val >= 0 ? '+' : ''}{val.toFixed(2)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Right: Gesprächsqualität */}
                  <div>
                    {avgSessions != null && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 4 }}>
                          <span style={{
                            fontSize: 28, fontWeight: 800, letterSpacing: '-1px', lineHeight: 1,
                            color: '#F59E0B',
                            fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                          }}>
                            {avgSessions.toFixed(1)}
                          </span>
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', paddingBottom: 2 }}>Sessions/Monat</span>
                        </div>
                        {freqScore != null && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', width: 55 }}>Frequenz</div>
                            <AnimatedBar value={freqScore} color={freqScore >= 70 ? '#22c55e' : freqScore >= 40 ? '#f59e0b' : '#ef4444'} delay={0} />
                            <div style={{
                              padding: '2px 7px', borderRadius: 20, fontSize: 9, fontWeight: 600,
                              background: freqScore >= 70 ? '#22c55e14' : freqScore >= 40 ? '#f59e0b14' : '#ef444414',
                              color: freqScore >= 70 ? '#22c55e' : freqScore >= 40 ? '#f59e0b' : '#ef4444',
                              border: `1px solid ${freqScore >= 70 ? '#22c55e40' : freqScore >= 40 ? '#f59e0b40' : '#ef444440'}`,
                            }}>
                              {freqScore.toFixed(0)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {avgRating != null && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 4 }}>
                          <span style={{
                            fontSize: 28, fontWeight: 800, letterSpacing: '-1px', lineHeight: 1,
                            color: scoreColor(avgRating),
                            fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                          }}>
                            {avgRating.toFixed(1)}
                          </span>
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', paddingBottom: 2 }}>Ø Bewertung / 5</span>
                        </div>
                        {qualScore != null && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', width: 55 }}>Qualität</div>
                            <AnimatedBar value={qualScore} color={qualScore >= 70 ? '#22c55e' : qualScore >= 40 ? '#f59e0b' : '#ef4444'} delay={80} />
                            <div style={{
                              padding: '2px 7px', borderRadius: 20, fontSize: 9, fontWeight: 600,
                              background: qualScore >= 70 ? '#22c55e14' : qualScore >= 40 ? '#f59e0b14' : '#ef444414',
                              color: qualScore >= 70 ? '#22c55e' : qualScore >= 40 ? '#f59e0b' : '#ef4444',
                              border: `1px solid ${qualScore >= 70 ? '#22c55e40' : qualScore >= 40 ? '#f59e0b40' : '#ef444440'}`,
                            }}>
                              {qualScore.toFixed(0)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Block 4: Interventionsbedarf ── */}
              <div style={{
                marginBottom: 20, padding: '16px 20px',
                background: intCount > 0 ? 'rgba(239,68,68,0.04)' : 'rgba(34,197,94,0.04)',
                border: `1px solid ${intCount > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)'}`,
                borderRadius: 14,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: intCount > 0 ? '#ef4444' : '#22c55e',
                  boxShadow: `0 0 8px ${intCount > 0 ? '#ef444488' : '#22c55e88'}`,
                  flexShrink: 0,
                }} />
                <div style={{ flex: 1 }}>
                  {intCount > 0 ? (
                    <>
                      <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                        {intCount} {intCount === 1 ? 'Pair' : 'Pairs'} mit Interventionsbedarf
                      </div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 3 }}>
                        Mentoring-Paare mit negativer oder stagnierender Kompetenzentwicklung
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>
                      Kein akuter Interventionsbedarf
                    </div>
                  )}
                </div>
                {intCount > 0 && (
                  <div style={{
                    padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                    background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                    color: '#ef4444', flexShrink: 0,
                  }}>
                    {intCount} {intCount === 1 ? 'Fall' : 'Fälle'}
                  </div>
                )}
              </div>

              {/* ── Fußzeile ── */}
              <div style={{
                padding: '10px 14px',
                background: 'rgba(255,255,255,0.02)',
                borderRadius: 9, borderLeft: '3px solid #F59E0B',
                fontSize: 11, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6,
              }}>
                Ramp-Up basiert auf Matching-Bestätigung bis Kennenlernen · Lernfortschritt über Feedback-Milestones · {menteesTotal} Mentees · {period}
              </div>
              </>)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
